import { useProjectParams } from '@hooks/useProjectParams';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimContractData, WorkOrderStatusEnum } from 'gql/graphql';
import { ProjectContractTypeEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { notFrameLowName } from 'pages/Project/Common/types';
import { Loader } from 'uikit';

import type React from 'react';
import { useEffect, useMemo } from 'react';

import { useBimContract } from '../hooks/useBimContract';

import { BimContractReadForm } from './components/BimContractReadForm';
import { BimContractToolbar } from './components/BimContractToolbar';

export const BimContractRead: React.FC = () => {
    const { id: projectId, methodology } = useProjectParams();

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const { projectData, projectDataStatus } = useProjectData(projectId);
    const actualContractId = useMemo(() => projectData?.contractId, [projectData?.contractId]);
    const { bimContractData, bimContractDataStatus, makeResetBimContract } = useBimContract(actualContractId);

    useEffect(() => {
        if (projectData && bimContractDataStatus === RequestStatus.SUCCESS) {
            sendGpnAnalyticsEvent({
                componentId: 'rim_contract page_onloaded',
                constaeventtype: 'undefined',
                component: 'OTHER',
                projectId: projectData.uniqueId,
                methodology: projectData.methodology,
                projectName: projectData.name,
                projectType: projectData.contractType,
                projectDO: projectData.subsidiary?.name,
                sendOnce: true,
            });
        }
    }, [bimContractDataStatus, projectData, sendGpnAnalyticsEvent]);

    const isLoading = useMemo(
        () => projectDataStatus === RequestStatus.FETCHING || bimContractDataStatus === RequestStatus.FETCHING,
        [bimContractDataStatus, projectDataStatus],
    );

    const isFrameContract = useMemo(
        () => projectData?.contractType === ProjectContractTypeEnum.Frame,
        [projectData?.contractType],
    );

    const isFrameContractWithWorkOrders = useMemo(
        () => isFrameContract && !!projectData?.contractNavigation?.data?.workOrderNavs?.length,
        [isFrameContract, projectData?.contractNavigation?.data?.workOrderNavs?.length],
    );

    const contractCalculation = useMemo(
        () =>
            !isFrameContract
                ? projectData?.contractNavigation?.data?.contractNavItems?.find(
                      (n) => n.name.toLowerCase() === notFrameLowName,
                  )
                : undefined,
        [isFrameContract, projectData?.contractNavigation?.data?.contractNavItems],
    );

    const contractCalculationStatus: WorkOrderStatusEnum | undefined = useMemo(
        () => contractCalculation?.metadata?.status,
        [contractCalculation?.metadata?.status],
    );

    const isNormalContractWithCalculation = useMemo(
        () =>
            !isFrameContract &&
            !!projectData?.contractNavigation?.data?.contractNavItems?.find(
                (n) => n.name.toLowerCase() === notFrameLowName,
            ),
        [isFrameContract, projectData?.contractNavigation?.data?.contractNavItems],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
        ],
        [methodology, projectData?.name, projectId],
    );

    useEffect(
        () => () => {
            makeResetBimContract(null);
        },
        [makeResetBimContract],
    );

    const hasWorkOrders = useMemo(
        () =>
            !!(
                projectData?.contractNavigation?.data?.workOrderNavs?.length ||
                projectData?.contractNavigation?.data?.contractNavData?.hasWorkOrders
            ),
        [
            projectData?.contractNavigation?.data?.contractNavData?.hasWorkOrders,
            projectData?.contractNavigation?.data?.workOrderNavs?.length,
        ],
    );

    return (
        <ProjectPageLayout
            title="Договор"
            breadCrumbsItems={breadCrumbsItems}
            isHeaderCollapsable
            headerBottom={
                <BimContractToolbar
                    projectId={projectId}
                    statusContract={bimContractData?.status}
                    statusProject={projectData?.status}
                />
            }
        >
            {isLoading && <Loader />}
            {!isLoading && (
                <BimContractReadForm
                    data={bimContractData as BimContractData}
                    isPieShow={hasWorkOrders}
                    contractType={projectData?.contractType as ProjectContractTypeEnum}
                    contractId={actualContractId}
                    isFrameContractWithWorkOrders={isFrameContractWithWorkOrders}
                    isNormalContractWithCalculation={isNormalContractWithCalculation}
                    contractCalculationStatus={contractCalculationStatus}
                />
            )}
        </ProjectPageLayout>
    );
};

BimContractRead.displayName = 'BimContractRead';
